// @ts-nocheck
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GlobalStyle, { ChildGlobals } from "@insite/mobius/GlobalStyle";
import * as React from "react";
import { ThemeProvider as SCThemeProvider } from "styled-components";

/* Starter Custom Code - Start */
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import useIsFirstRender from "@toolkit/Framework/Common/Hooks/useIsFirstRender";
import isShellTheme from "@toolkit/Framework/Common/Utilities/isShellTheme";
import overrideProps from "@toolkit/Framework/Common/Utilities/overrideProps";
import { themeCollection } from "@toolkit/Framework/ThemeConfiguration";
import Styleguide from "@toolkit/Framework/Types/Styleguide";
/* Starter Custom Code - End */

export interface ExtendedTheme extends BaseTheme {
    [key: string]: any;
}

export interface ThemeProviderProps {
    children?: React.ReactNode;
    /** Determines whether the global styles will be created for the site based on this theme. */
    createGlobalStyle?: boolean;
    /** Determines whether children will be provided text styles based on this theme. */
    createChildGlobals?: boolean;
    theme: ExtendedTheme;
    /** Function to localize UI strings. */
    translate: (text: string) => string;
}

type ThemeProviderPropsCompleted = Omit<ThemeProviderProps, "translate"> &
    Required<Pick<ThemeProviderProps, "translate">>;

/**
 * Exposes a theme object to its child components, and creates global CSS rules based on the theme.
 */
const ThemeProvider: React.FC<ThemeProviderPropsCompleted> = ({
    children,
    theme,
    createChildGlobals,
    createGlobalStyle,
    translate = (text: string) => text,
}) => {
    theme!.translate = translate;

    /* Starter Custom Code - Start */
    const isFirstRender = useIsFirstRender();
    let areSettingsLoaded = true;
    let activeTheme: Styleguide | undefined;

    if (!isShellTheme(theme)) {
        areSettingsLoaded = useAppSelector(state => {
            const value = state.context?.areSettingsLoaded;
            return typeof value === "undefined" ? true : value;
        });

        activeTheme = useAppSelector(state => {
            const theme = state.context?.settings?.settingsCollection?.extensionWebsiteSettings?.theme;
            return theme ? themeCollection[theme] : undefined;
        });
    }

    const extendTheme = React.useCallback(
        () => overrideProps(theme, activeTheme) as Toolkit.Theme,
        [theme, activeTheme],
    );

    const [extendedTheme, setExtendedTheme] = React.useState(extendTheme());

    React.useEffect(() => {
        if (!isFirstRender) {
            setExtendedTheme(extendTheme());
        }
    }, [theme]);

    React.useEffect(() => {
        if (!isFirstRender && activeTheme?.name !== extendedTheme.name) {
            setExtendedTheme(extendTheme());
        }
    }, [activeTheme]);
    /* Starter Custom Code - End */

    return (
        <SCThemeProvider theme={extendedTheme /* Starter Custom Code */}>
            <>
                {areSettingsLoaded && createGlobalStyle && <GlobalStyle /> /* Starter Custom Code */}
                {createChildGlobals && <ChildGlobals data-id="childGlobals">{children}</ChildGlobals>}
                {!createChildGlobals && children}
            </>
        </SCThemeProvider>
    );
};

ThemeProvider.defaultProps = {
    createGlobalStyle: false,
    createChildGlobals: true,
    theme: baseTheme,
};

export default ThemeProvider;
